import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formService } from '@/services';
import useAuth from '@/hooks/stores/useAuth';
import DynamicForm from './DynamicForm';
import { Clock, Video, RefreshCw, ArrowLeft, FileText, AlertCircle, CheckCircle2, Loader } from 'lucide-react';

export const Form = () => {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'video_not_found', 'transcription_not_found', 'form_not_found'
  const userId = useAuth((state) => state.userId);
  const navigate = useNavigate();

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        setError(null);
        setErrorType(null);
        
        // Try user-specific form first
        let data;
        try {
          data = await formService.getUserForm(userId);
          setFormData(data.data || data);
        } catch (err) {
          console.log('User-specific form error:', err);
          
          // Check for specific error messages from backend
          const errorMessage = err.response?.data?.message || err.message || '';
          
          if (errorMessage === "No video found") {
            setErrorType('video_not_found');
            setError("Your video is pending approval from the admin. Please wait for approval before accessing the form.");
            return;
          } else if (errorMessage === "No transcription found") {
            setErrorType('transcription_not_found');
            setError("Your video has been approved! We're currently working on the transcription. Please check back soon.");
            return;
          } else if (errorMessage === "Form not found" || err.response?.status === 404) {
            setErrorType('form_not_found');
            setError("Your transcription is complete, but the form is not available at the moment. Please contact support.");
            return;
          }
          
          // For other errors, try fallback to default form
          console.log('Trying default form as fallback...');
          try {
            data = await formService.getForm(1);
            setFormData(data.data || data);
          } catch (fallbackErr) {
            console.error('Fallback form also failed:', fallbackErr);
            setError("Unable to load form. Please try again later.");
            setErrorType('general_error');
          }
        }
      } catch (err) {
        console.error('Failed to retrieve form data:', err);
        setError("Failed to retrieve form data");
        setErrorType('general_error');
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, [userId]);

  const handleRetry = () => {
    window.location.reload();
  };

  const handleBackToDashboard = () => {
    navigate('/user-dashboard');
  };

  // Render specific error states based on error type
  const renderErrorState = () => {
    let icon, title, description, iconBgColor, iconColor;

    switch (errorType) {
      case 'video_not_found':
        icon = <Video className="w-8 h-8" />;
        iconBgColor = 'bg-yellow-100';
        iconColor = 'text-yellow-600';
        title = 'Video Approval Pending';
        description = "Your video is currently under review by our admin team. Once approved, you'll be able to access your personalized form.";
        break;
      
      case 'transcription_not_found':
        icon = <Loader className="w-8 h-8 animate-spin" />;
        iconBgColor = 'bg-blue-100';
        iconColor = 'text-blue-600';
        title = 'Transcription in Progress';
        description = "Great news! Your video has been approved. We're currently processing the transcription to create your personalized form.";
        break;
      
      case 'form_not_found':
        icon = <AlertCircle className="w-8 h-8" />;
        iconBgColor = 'bg-orange-100';
        iconColor = 'text-orange-600';
        title = 'Form Temporarily Unavailable';
        description = "Your transcription has been completed, but the form is temporarily unavailable.";
        break;
      
      default:
        icon = <FileText className="w-8 h-8" />;
        iconBgColor = 'bg-secondary';
        iconColor = 'text-muted-foreground';
        title = 'Unable to Load Form';
        description = 'We encountered an issue loading your form. This might be a temporary problem.';
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-auto px-4">
          <div className={`w-16 h-16 ${iconBgColor} rounded-full flex items-center justify-center mx-auto mb-6`}>
            <div className={iconColor}>
              {icon}
            </div>
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-3">{title}</h3>
          <p className="text-muted-foreground mb-6 leading-relaxed">
            {description}
          </p>
          
          {/* Show different actions based on error type */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {errorType === 'video_not_found' && (
              <>
                <button 
                  onClick={handleBackToDashboard}
                  className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium flex items-center justify-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </button>
              </>
            )}
            
            {errorType === 'transcription_not_found' && (
              <>
                <button 
                  onClick={handleRetry}
                  className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium flex items-center justify-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Check Again
                </button>
                <button 
                  onClick={handleBackToDashboard}
                  className="px-6 py-3 border border-secondary-border text-muted-foreground rounded-lg hover:text-foreground hover:bg-secondary transition-colors font-medium flex items-center justify-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </button>
              </>
            )}
            
            {(errorType === 'form_not_found' || errorType === 'general_error') && (
              <>
                <button 
                  onClick={handleRetry}
                  className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium flex items-center justify-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </button>
                <button 
                  onClick={handleBackToDashboard}
                  className="px-6 py-3 border border-secondary-border text-muted-foreground rounded-lg hover:text-foreground hover:bg-secondary transition-colors font-medium flex items-center justify-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Loading Your Form</h3>
          <p className="text-muted-foreground">Please wait while we prepare your form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return renderErrorState();
  }

  if (!formData) {
    return renderErrorState();
  }

  return <DynamicForm formData={formData} />;
}; 